import { StatusBadge } from "@/components/StatusBadge";
import { RaiseActionButton } from "@/components/RaiseAction";
import { fmtNum, fmtPct, STATUS_HEX } from "@/lib/format";

function Spark({ values }) {
  if (!values?.length) return <span className="text-slate-600">—</span>;
  const w = 70, h = 22;
  const pts = values.map((v, i) => `${(i / Math.max(values.length - 1, 1)) * w},${h - (Math.min(v, 120) / 120) * h}`).join(" ");
  return (
    <svg width={w} height={h} className="overflow-visible">
      <line x1="0" x2={w} y1={h - (95 / 120) * h} y2={h - (95 / 120) * h} stroke="#CBD5E1" strokeDasharray="2 2" />
      <polyline points={pts} fill="none" stroke="#3B82F6" strokeWidth="1.5" />
      {values.map((v, i) => <circle key={i} cx={(i / Math.max(values.length - 1, 1)) * w} cy={h - (Math.min(v, 120) / 120) * h} r="2" fill={v >= 95 ? STATUS_HEX.green : v >= 80 ? STATUS_HEX.amber : STATUS_HEX.red} />)}
    </svg>
  );
}

export function LineMatrix({ lines, reportingDate }) {
  return (
    <div className="overflow-x-auto" data-testid="line-matrix">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="text-left pp-label border-b border-slate-200">
            <th className="py-2.5 pr-3">Line</th>
            <th className="py-2.5 pr-3">Model</th>
            <th className="py-2.5 pr-3 text-right">Target</th>
            <th className="py-2.5 pr-3 text-right" title="Daily Plan pack plan for the day">Day plan</th>
            <th className="py-2.5 pr-3 text-right">Actual</th>
            <th className="py-2.5 pr-3 text-right">Ach %</th>
            <th className="py-2.5 pr-3 text-right">OEE</th>
            <th className="py-2.5 pr-3 text-right">FPY</th>
            <th className="py-2.5 pr-3 text-right">DT min</th>
            <th className="py-2.5 pr-3 text-right">Defects</th>
            <th className="py-2.5 pr-3">Trend</th>
            <th className="py-2.5 pr-3">Reported</th>
            <th className="py-2.5">Status</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => (
            <tr key={l.line_id} data-testid={`line-row-${l.line_code}`} className={`border-b border-slate-200 hover:bg-slate-50 transition-colors ${l.missing_hours.length ? "bg-rose-50" : ""}`}>
              <td className="py-2.5 pr-3 font-semibold whitespace-nowrap"><span className="font-mono text-blue-700 mr-2">{l.line_code}</span>{l.line_name}<span className="ml-2 text-xs text-slate-9000 font-normal">{l.section_name}</span></td>
              <td className="py-2.5 pr-3 text-slate-500 whitespace-nowrap text-xs">{l.models?.join(", ") || "—"}{l.ot_planned > 0 && <span className="ml-2 rounded bg-amber-100 px-1 text-[9px] font-bold text-amber-700" data-testid={`ot-badge-${l.line_code}`}>OT {l.ot_records}/{l.ot_planned}h</span>}</td>
              <td className="py-2.5 pr-3 text-right font-mono">{fmtNum(l.target)}</td>
              <td className="py-2.5 pr-3 text-right font-mono whitespace-nowrap" data-testid={`day-plan-${l.line_code}`}>{l.day_plan ? <>{fmtNum(l.day_plan)}<span className="ml-1 text-[10px]" style={{ color: STATUS_HEX[l.day_plan_status] || "#64748b" }}>{l.day_plan_pct != null ? fmtPct(l.day_plan_pct) : ""}</span></> : <span className="text-slate-300">—</span>}</td>
              <td className="py-2.5 pr-3 text-right font-mono font-semibold">{fmtNum(l.actual)}</td>
              <td className="py-2.5 pr-3 text-right font-mono" style={{ color: STATUS_HEX[l.status] }}>{l.records ? fmtPct(l.achievement_pct) : "—"}</td>
              <td className="py-2.5 pr-3 text-right font-mono">{l.records ? fmtPct(l.oee) : "—"}</td>
              <td className="py-2.5 pr-3 text-right font-mono">{l.records ? fmtPct(l.fpy) : "—"}</td>
              <td className="py-2.5 pr-3 text-right font-mono">{l.records ? l.downtime_min : "—"}</td>
              <td className="py-2.5 pr-3 text-right font-mono">{l.records ? l.defects : "—"}</td>
              <td className="py-2.5 pr-3"><Spark values={l.sparkline} /></td>
              <td className="py-2.5 pr-3 font-mono text-xs whitespace-nowrap">
                <span className={l.missing_hours.length ? "text-rose-600" : "text-emerald-600"}>{l.records} row(s)</span>
                {l.missing_hours.length > 0 && <span className="ml-2 text-rose-600" title={l.missing_hours.join(", ")} data-testid={`missing-flag-${l.line_code}`}>⚑ not reported</span>}
              </td>
              <td className="py-2.5"><div className="flex items-center gap-2"><StatusBadge status={l.status} />{(l.status === "red" || l.status === "amber" || l.status === "missing") && <RaiseActionButton line={l} reportingDate={reportingDate} />}</div></td>
            </tr>
          ))}
          {!lines.length && <tr><td colSpan={13} className="py-8 text-center text-slate-9000">No lines match the current filters</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
